import { useEffect, useState, useCallback } from "react";
import { Link, useOutletContext, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Card({ children, className = "", ...props }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`} {...props}>{children}</div>;
}

function Stat({ icon, label, value, sub }) {
  const Ic = Icons[icon];
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#F5A623]/15 flex items-center justify-center shrink-0"><Ic className="h-5 w-5 text-[#d98b12]" /></div>
        <div className="min-w-0">
          <div className="text-2xl font-bold text-[#171B2E] leading-none">{value}</div>
          <div className="text-xs text-slate-500 mt-1 leading-tight">{label}{sub ? <span className="text-slate-400"> · {sub}</span> : ""}</div>
        </div>
      </div>
    </Card>
  );
}

// ── OrganizerStat — Organizer quick stat with compact submitted indicator ──────
// Shows "Submitted ✓  View →" only when organizer.submitted is true AND the
// state is not an actionable staff-initiated state (reopened/waiting_on_client).
// In those cases the standalone OrganizerCTA banner takes precedence.
function OrganizerStat({ pct, organizer }) {
  const submitted = organizer?.submitted;
  const state = organizer?.cta_state;
  // Submitted ✓ display is suppressed when an actionable state overrides it
  const showSubmitted = submitted && state !== "reopened" && state !== "waiting_on_client";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#F5A623]/15 flex items-center justify-center shrink-0">
          <Icons.Gauge className="h-5 w-5 text-[#d98b12]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-2xl font-bold text-[#171B2E] leading-none" data-testid="portal-organizer-stat-pct">{pct}%</div>
          <div className="text-xs text-slate-500 mt-1 leading-tight">Organizer progress</div>
          {showSubmitted && (
            <div className="flex items-center gap-2 mt-1.5" data-testid="portal-organizer-submitted-badge">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                <Icons.CheckCircle2 className="h-3 w-3" />Submitted
              </span>
              <Link to="/portal/organizer"
                className="text-xs font-medium text-[#d98b12] hover:text-[#b8750e] hover:underline"
                data-testid="portal-organizer-view-link">
                View →
              </Link>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── OrganizerCTA — shown only for staff-initiated actionable states ────────────
// States: reopened, waiting_on_client only. submitted is handled in OrganizerStat.
// not_started / in_progress: no standalone banner; organizer accessible via nav.
function OrganizerCTA({ organizer }) {
  const state = organizer.cta_state;
  const progress = organizer.progress ?? 0;
  const ctaLabel = organizer.cta_label || "Review Organizer";
  const remaining = organizer.required_remaining ?? 0;

  const reopened = state === "reopened";
  return (
    <div className={`rounded-2xl border-2 p-4 shadow-sm ${reopened ? "border-amber-400 bg-amber-50" : "border-[#F5A623]/50 bg-gradient-to-br from-[#F5A623]/12 to-white"}`}
         data-testid="portal-organizer-cta" data-cta-state={state}>
      <div className="flex items-start gap-3 flex-col sm:flex-row">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${reopened ? "bg-amber-400" : "bg-[#F5A623]"}`}>
          <Icons.ClipboardList className="h-5 w-5 text-[#171B2E]" />
        </div>
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#b9760a]">Action requested</span>
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="portal-organizer-cta-flag">
              {reopened ? "Reopened" : "Updates needed"}
            </span>
          </div>
          <div className="font-heading text-base font-bold text-[#171B2E] mt-0.5" data-testid="portal-organizer-cta-title">
            {reopened ? "Organizer Reopened — Updates Needed" : "Your Tax Organizer Needs Attention"}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {reopened
              ? "Your tax team reopened your organizer. Please review, update, and resubmit."
              : "Your tax team has asked you to review or update a few answers."}
          </p>
          {progress > 0 && (
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#F5A623] transition-[width] duration-700" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
              </div>
              <span className="text-sm font-semibold text-[#171B2E]" data-testid="portal-organizer-cta-progress">{progress}%</span>
            </div>
          )}
          {remaining > 0 && (
            <p className="text-xs text-slate-500 mt-1" data-testid="portal-organizer-cta-remaining">
              {remaining} required question{remaining === 1 ? "" : "s"} remaining
            </p>
          )}
          <Link to="/portal/organizer" data-testid="portal-organizer-cta-btn"
            className="mt-3 inline-flex items-center gap-1.5 bg-[#171B2E] hover:bg-[#222842] text-white text-sm font-semibold rounded-lg px-4 py-2">
            {ctaLabel}<Icons.ArrowRight className="h-4 w-4 text-[#F5A623]" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ percent, tone = "#F5A623", height = "h-2.5" }) {
  const pct = Math.max(0, Math.min(100, percent || 0));
  return (
    <div className={`w-full ${height} rounded-full bg-slate-100 overflow-hidden`}>
      <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${pct}%`, backgroundColor: tone }} />
    </div>
  );
}

const RESP_STYLES = {
  client:      { pill: "bg-amber-50 border-amber-200 text-amber-700",  icon: "AlertCircle",   border: "border-amber-200 bg-amber-50/30" },
  firm:        { pill: "bg-blue-50 border-blue-200 text-blue-700",     icon: "Clock",         border: "border-slate-200 bg-white" },
  third_party: { pill: "bg-slate-50 border-slate-200 text-slate-600",  icon: "Globe",         border: "border-slate-200 bg-white" },
  complete:    { pill: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: "CheckCircle2", border: "border-emerald-100 bg-emerald-50/30" },
};

function EngagementLifecycleCard({ card }) {
  const resp   = card.responsibility?.actor || "firm";
  const styles = RESP_STYLES[resp] || RESP_STYLES.firm;
  const RespIc = Icons[styles.icon] || Icons.Circle;
  return (
    <div className={`rounded-2xl border p-4 transition-all ${styles.border}`} data-testid={`engagement-card-${card.engagement_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#171B2E] truncate" data-testid={`eng-card-title-${card.engagement_id}`}>
            {card.year_label || card.service_title}
          </div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid={`eng-card-stage-${card.engagement_id}`}>{card.stage?.label}</div>
        </div>
        <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${styles.pill}`} data-testid={`eng-card-resp-${card.engagement_id}`}>
          <RespIc className="h-3 w-3" />
          {card.responsibility?.label}
        </span>
      </div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid={`eng-card-detail-${card.engagement_id}`}>
        {card.stage?.detail}
      </p>
      {card.outstanding_action?.exists && card.outstanding_action?.link && (
        <Link to={card.outstanding_action.link} data-testid={`eng-card-cta-${card.engagement_id}`}
          className="mt-3 inline-flex items-center gap-1.5 bg-[#171B2E] hover:bg-[#222842] text-white text-xs font-semibold rounded-lg px-3 py-2 transition-colors">
          {card.outstanding_action.cta_label}
          <Icons.ArrowRight className="h-3.5 w-3.5 text-[#F5A623]" />
        </Link>
      )}
    </div>
  );
}

// ── ActionRow — full-width single action item with description below ───────────
function ActionRow({ it }) {
  const Ic = Icons[it.icon] || Icons.Circle;
  const TONES = {
    blue: "bg-blue-50 border-blue-200 text-blue-700", amber: "bg-amber-50 border-amber-200 text-amber-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700", rose: "bg-rose-50 border-rose-200 text-rose-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700", emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
  };
  const toneClass = TONES[it.tone] || TONES.blue;
  return (
    <Link to={it.link} data-testid={`portal-action-${it.key}`}
      className={`flex flex-col rounded-xl border px-4 py-3 hover:shadow-md transition-all group ${toneClass}`}>
      <div className="flex items-center gap-3">
        <Ic className="h-5 w-5 shrink-0" />
        <span className="text-sm font-semibold text-[#171B2E] flex-1 min-w-0">{it.label}</span>
        <span className="text-xs font-semibold inline-flex items-center gap-1 shrink-0 group-hover:gap-1.5 transition-all whitespace-nowrap">
          {it.cta}<Icons.ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
      {it.description && (
        <p className="text-sm text-slate-600 mt-1.5 ml-8 leading-relaxed">{it.description}</p>
      )}
    </Link>
  );
}

export default function PortalDashboard() {
  const [data, setData] = useState(null);
  const [actions, setActions] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [engagements, setEngagements] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [acceptErr, setAcceptErr] = useState(null);
  const [paymentBanner, setPaymentBanner] = useState(null);
  const [onboardingBanner, setOnboardingBanner] = useState(false);
  const { branding } = useOutletContext() || {};
  const location = useLocation();
  const navigate = useNavigate();

  const loadActions = useCallback(() => {
    portalApi.get("/portal/action-center").then((r) => setActions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    portalApi.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {});
    loadActions();
    portalApi.get("/portal/organizer").then((r) => setOrganizer(r.data?.organizer || null)).catch(() => {});
    portalApi.get("/portal/engagements-status").then((r) => setEngagements(r.data?.engagements || [])).catch(() => setEngagements([]));
    const dismissed = sessionStorage.getItem("tf_onboarding_dismissed");
    portalApi.get("/portal/onboarding-status").then((r) => {
      if (r.data?.active) {
        if (!dismissed) navigate("/portal/onboarding", { replace: true });
        else setOnboardingBanner(true);
      }
    }).catch(() => {});
  }, [loadActions, navigate]);

  useEffect(() => {
    const state = location.state;
    if (!state?.paymentConfirmed) return;
    setPaymentBanner(state.paymentInfo || true);
    loadActions();
    const t = setTimeout(() => setPaymentBanner(null), 8000);
    return () => clearTimeout(t);
  }, [location.state, loadActions]);

  if (!data || !actions) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;

  const s = data.stats || {};
  const cm = data.case_manager;
  const ep = data.engagement_progress || {};
  const dp = data.document_progress || {};

  // 4th Quick Stat: use first active engagement's client-safe stage label from
  // the Engagement Status Mirror; fall back to formatted lifecycle_status.
  // engagements array order is authoritative (returned by /portal/engagements-status).
  const primaryStageLabel =
    (engagements ?? []).find((c) => !c.is_complete)?.stage?.label ||
    (data.lifecycle_status || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // OrganizerCTA is only rendered for staff-initiated actionable states.
  const orgState = organizer?.cta_state;
  const showOrgCTA = organizer && (orgState === "reopened" || orgState === "waiting_on_client");

  const TONES = {
    blue: "bg-blue-50 border-blue-200 text-blue-700", amber: "bg-amber-50 border-amber-200 text-amber-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700", rose: "bg-rose-50 border-rose-200 text-rose-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700", emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
  };

  return (
    <div className="space-y-4" data-testid="portal-dashboard">

      {/* Welcome heading */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#171B2E]" data-testid="portal-welcome">Welcome back, {data.welcome_name}</h1>
        <p className="text-slate-500 text-sm mt-1">Here's the latest on your tax engagement.</p>
      </div>

      {/* Transient banners */}
      {paymentBanner && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3" data-testid="portal-payment-confirmed-banner">
          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">Payment received — thank you!</p>
            {paymentBanner?.invoiceNumber && (
              <p className="text-xs text-emerald-700 mt-0.5">
                {paymentBanner.invoiceNumber}
                {paymentBanner.amount != null
                  ? ` · ${new Intl.NumberFormat("en-US", { style: "currency", currency: paymentBanner.currency || "USD" }).format(paymentBanner.amount)}`
                  : ""}
                {" "}has been marked paid. Your balance will reflect shortly.
              </p>
            )}
          </div>
          <button onClick={() => setPaymentBanner(null)} className="ml-auto text-emerald-500 hover:text-emerald-700 shrink-0" data-testid="portal-payment-banner-dismiss">
            <Icons.X className="h-4 w-4" />
          </button>
        </div>
      )}

      {onboardingBanner && (
        <div className="bg-[#171B2E] border border-[#F5A623]/30 rounded-2xl p-4 flex items-start gap-3" data-testid="portal-onboarding-resume-banner">
          <div className="h-8 w-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center shrink-0">
            <Icons.ClipboardList className="h-4 w-4 text-[#F5A623]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Your onboarding isn't complete yet</p>
            <p className="text-xs text-slate-400 mt-0.5">Complete your engagement setup to get started with your tax return.</p>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem("tf_onboarding_dismissed"); navigate("/portal/onboarding"); }}
            className="shrink-0 inline-flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors"
            data-testid="portal-onboarding-resume-btn">
            Continue Onboarding <Icons.ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* ── 1. Overall Engagement Progress — compact headline card ────────── */}
      <Card className="p-4" data-testid="portal-progress-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icons.Gauge className="h-4 w-4 text-[#F5A623]" />
            <h2 className="font-heading text-base font-semibold text-[#171B2E]" data-testid="portal-progress-title">
              {data.lifecycle_status === "lead" ? "Getting Started" : "Overall Engagement Progress"}
            </h2>
          </div>
          <span className="text-xl font-bold text-[#171B2E] leading-none" data-testid="portal-engagement-percent">{ep.percent ?? 0}%</span>
        </div>
        <ProgressBar percent={ep.percent} height="h-2.5" />
        <div className="text-xs text-slate-500 mt-1.5">{ep.label || "Getting started"}</div>
      </Card>

      {/* ── 2. Four Quick Stats — grouped tight below Progress ────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon="FileCheck2" label="Documents uploaded" value={s.documents_uploaded ?? 0} sub={`of ${s.documents_total ?? 0} required`} />
        <OrganizerStat pct={s.org_progress ?? 0} organizer={organizer} />
        <Stat icon="MessageSquare" label="Unread messages" value={s.unread_messages ?? 0} />
        {/* 4th stat: primary active engagement stage (from Engagement Status Mirror) or lifecycle_status */}
        <Stat icon="ClipboardList" label="Current Stage" value={primaryStageLabel} />
      </div>

      {/* ── Organizer CTA — only for staff-initiated actionable states ───── */}
      {showOrgCTA && <OrganizerCTA organizer={organizer} />}

      {/* ── 3. Waiting on You | Your Advisor — 67/33 ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

        {/* Waiting on You — lg:col-span-2 (~67%) */}
        <Card
          className={`lg:col-span-2 p-4 ${(actions?.count ?? 0) > 0 ? "border-[#F5A623]/40 bg-gradient-to-br from-[#F5A623]/8 to-white" : ""}`}
          data-testid="portal-waiting-on-you">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-[#F5A623] flex items-center justify-center shrink-0">
              <Icons.CheckCircle2 className="h-4 w-4 text-[#171B2E]" />
            </div>
            <h2 className="font-heading text-base font-semibold text-[#171B2E]">Waiting on You</h2>
            {(actions?.count ?? 0) > 0 && (
              <span className="ml-0.5 text-xs font-semibold bg-[#171B2E] text-white rounded-full px-2 py-0.5" data-testid="portal-waiting-count">{actions.count}</span>
            )}
          </div>

          {(actions?.count ?? 0) === 0 ? (
            <div className="flex items-center gap-3 py-1" data-testid="portal-waiting-empty">
              <div className="h-7 w-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="text-sm text-slate-500">You're all caught up — no actions needed right now.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {actions.items.map((it) => {
                const Ic = Icons[it.icon] || Icons.Circle;
                const isEstimate = it.type === "estimate";
                const eid = isEstimate ? it.key.replace("estimate-", "") : null;
                const isConfirming = isEstimate && confirming === eid;
                const isAccepting = isEstimate && accepting === eid;
                const toneClass = (it.type === "estimate" || it.type === "invoice")
                  ? `border-l-4 ${it.tone === "rose" ? "border-l-rose-500 bg-rose-50 border-rose-200"
                      : it.tone === "amber" ? "border-l-amber-500 bg-amber-50 border-amber-200"
                      : "border-l-emerald-500 bg-emerald-50 border-emerald-200"}`
                  : TONES[it.tone] || TONES.blue;

                if (!isEstimate) return <ActionRow key={it.key} it={it} />;

                // Estimate inline-accept flow
                const handleAccept = async () => {
                  setAcceptErr(null); setAccepting(eid);
                  try {
                    await portalApi.post(`/portal/estimates/${eid}/accept`, { agree: true });
                    setActions((prev) => {
                      if (!prev) return prev;
                      const next = prev.items.filter((x) => x.key !== it.key);
                      return { ...prev, items: next, count: next.length };
                    });
                    setConfirming(null);
                  } catch (err) {
                    const detail = err?.response?.data?.detail;
                    setAcceptErr(typeof detail === "string" ? detail : detail?.message || "Could not accept estimate. Please try from the estimate page.");
                  } finally { setAccepting(null); }
                };

                return (
                  <div key={it.key} data-testid={`portal-action-${it.key}`}
                    className={`rounded-xl border px-3.5 py-3 transition-all ${toneClass}`}>
                    <div className="flex items-start gap-3">
                      <Ic className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-[#171B2E] leading-tight block">{it.label}</span>
                        {it.description && <span className="text-sm text-slate-600 block mt-0.5 truncate">{it.description}</span>}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {isConfirming ? (
                        <>
                          <span className="text-xs font-semibold text-[#171B2E] mr-1" data-testid={`portal-estimate-confirm-prompt-${eid}`}>Accept this estimate?</span>
                          <button onClick={handleAccept} disabled={isAccepting} data-testid={`portal-estimate-confirm-yes-${eid}`}
                            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors">
                            {isAccepting ? <><Icons.Loader2 className="h-3 w-3 animate-spin" />Accepting…</> : <><Icons.Check className="h-3 w-3" />Yes, Accept</>}
                          </button>
                          <button onClick={() => { setConfirming(null); setAcceptErr(null); }} disabled={isAccepting} data-testid={`portal-estimate-confirm-cancel-${eid}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
                            Cancel
                          </button>
                          {acceptErr && <p className="w-full text-xs text-rose-600 mt-1" data-testid={`portal-estimate-accept-error-${eid}`}>{acceptErr}</p>}
                        </>
                      ) : (
                        <>
                          <Link to={it.link} data-testid={`portal-estimate-view-${eid}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 border border-slate-300 bg-white rounded-lg px-3 py-1.5 transition-colors">
                            View Estimate<Icons.ArrowRight className="h-3 w-3" />
                          </Link>
                          <button onClick={() => { setConfirming(eid); setAcceptErr(null); }} data-testid={`portal-estimate-accept-btn-${eid}`}
                            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors">
                            <Icons.CheckCircle2 className="h-3 w-3" />Accept
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Your Advisor — lg:col-span-1 (~33%) */}
        <Card className="p-4 self-start">
          <div className="flex items-center gap-2 mb-3">
            <Icons.UserCircle2 className="h-4 w-4 text-[#F5A623]" />
            <h2 className="font-heading text-base font-semibold text-[#171B2E]">Your Advisor</h2>
          </div>
          {cm ? (
            <div data-testid="portal-advisor">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-[#171B2E] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {(cm.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#171B2E] text-sm truncate">{cm.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{cm.title}</div>
                </div>
              </div>
              <Link to="/portal/messages"
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#171B2E] hover:bg-[#222842] text-white text-sm font-semibold rounded-xl py-2.5 transition-colors"
                data-testid="portal-message-advisor-btn">
                <Icons.Send className="h-4 w-4 text-[#F5A623]" />Message your advisor
              </Link>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-1" data-testid="portal-no-advisor">An advisor will be assigned shortly.</p>
          )}
        </Card>
      </div>

      {/* ── 4. Document Collection — conditional, tighter padding ─────────── */}
      {(dp.outstanding ?? 0) > 0 && (
        <Card className="p-4 border-emerald-200" data-testid="portal-document-collection">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="h-9 w-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                <Icons.FolderUp className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-heading font-semibold text-[#171B2E] text-sm">Document Collection</span>
                  <span className="text-sm font-semibold text-[#171B2E] shrink-0" data-testid="portal-docs-percent">{dp.uploaded ?? 0} of {dp.total ?? 0}</span>
                </div>
                <ProgressBar percent={dp.percent} tone="#10b981" height="h-1.5" />
                <p className="text-xs text-slate-600 mt-1.5" data-testid="portal-docs-nudge">
                  <span className="font-semibold text-[#171B2E]">{dp.outstanding}</span> document{dp.outstanding === 1 ? "" : "s"} still needed. Uploading keeps your filing on schedule.
                </p>
              </div>
            </div>
            <Link to="/portal/documents" data-testid="portal-upload-cta"
              className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0991a] text-[#171B2E] text-sm font-semibold rounded-xl px-4 py-2 shrink-0 self-end sm:self-center transition-colors">
              <Icons.Upload className="h-4 w-4" />Upload Documents
            </Link>
          </div>
        </Card>
      )}

      {/* ── 5. Your Active Work | Upcoming Meetings ───────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

        {/* Your Active Work — lg:col-span-2, self-start */}
        <Card className="lg:col-span-2 p-4 self-start" data-testid="portal-active-work">
          <div className="flex items-center gap-2 mb-3">
            <Icons.LayoutList className="h-4 w-4 text-[#F5A623]" />
            <h2 className="font-heading text-base font-semibold text-[#171B2E]">Your Active Work</h2>
          </div>
          {engagements === null ? (
            <div className="flex items-center justify-center py-6" data-testid="portal-active-work-loading">
              <Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623]" />
            </div>
          ) : engagements.length === 0 ? (
            <p className="text-sm text-slate-400 py-2" data-testid="portal-active-work-empty">No active engagements yet.</p>
          ) : (
            <div className="space-y-3" data-testid="portal-engagement-cards">
              {engagements.filter((c) => !c.is_complete).map((card) => (
                <EngagementLifecycleCard key={card.engagement_id} card={card} />
              ))}
              {engagements.some((c) => c.is_complete) && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid="portal-completed-engagements">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Completed</div>
                  {engagements.filter((c) => c.is_complete).map((card) => (
                    <EngagementLifecycleCard key={card.engagement_id} card={card} />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Upcoming Meetings — self-start, natural height */}
        <Card className="p-4 self-start" data-testid="portal-meetings">
          <div className="flex items-center gap-2 mb-3">
            <Icons.CalendarClock className="h-4 w-4 text-[#F5A623]" />
            <h2 className="font-heading text-base font-semibold text-[#171B2E]">Upcoming Meetings</h2>
          </div>
          {(data.upcoming_meetings || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-4 text-center" data-testid="portal-meetings-empty">
              <Icons.CalendarX2 className="h-7 w-7 text-slate-200 mb-1.5" />
              <p className="text-sm text-slate-400">No meetings scheduled.</p>
              <p className="text-xs text-slate-400 mt-0.5">We'll let you know when one is scheduled.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.upcoming_meetings.map((m) => (
                <div key={m.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5">
                  <div className="text-center bg-white rounded-md px-2 py-1 border border-slate-200 shrink-0">
                    <div className="text-[10px] text-slate-400 uppercase">{fmtDate(m.scheduled_start).split(" ")[0]}</div>
                    <div className="text-sm font-bold text-[#171B2E]">{fmtDate(m.scheduled_start).split(" ")[1]}</div>
                  </div>
                  <div className="text-sm text-[#171B2E]">{m.title || "Consultation"}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── 6. Recent Activity — content-dense ───────────────────────────── */}
      <Card className="p-4" data-testid="portal-recent-activity">
        <div className="flex items-center gap-2 mb-3">
          <Icons.Activity className="h-4 w-4 text-[#F5A623]" />
          <h2 className="font-heading text-base font-semibold text-[#171B2E]">Recent Activity</h2>
        </div>
        {(data.recent_activity || []).length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="portal-activity-empty">No activity yet.</p>
        ) : (
          <div className="space-y-2" data-testid="portal-activity">
            {data.recent_activity.slice(0, 6).map((e) => (
              <div key={e.id} className="flex items-start gap-2.5">
                <div className="h-5 w-5 rounded-full bg-[#F5A623]/15 flex items-center justify-center mt-0.5 shrink-0">
                  <Icons.Dot className="h-4 w-4 text-[#F5A623]" />
                </div>
                <div>
                  <div className="text-sm text-[#171B2E] leading-snug">{e.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{fmtDateTime(e.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

    </div>
  );
}
